import { ApolloError } from '@apollo/client';
import { Box } from '@zoopla-eng/zds/box';
import { ButtonLeft } from '@zoopla-eng/zds/button';
import { RefreshMedium } from '@zoopla-eng/zds/icons';
import ListingCard, {
    getAmenities,
    getBadgeValues,
    getBranchContactUrl,
    getDetailsLink,
    getListingImages,
    getListingType,
    getPremiumHighlights,
    getStatus
} from '@zoopla/listing-card';
import {
    AnalyticsEcommerce,
    AnalyticsTaxonomy,
    Favourites,
    SearchCategory,
    SearchContextType,
    SearchSource
} from '@zoopla/listing-search-context';
import { NewHomesListings } from '@zoopla/listing-search/new-homes-listings';
import {
    segmentSearchEvent,
    segmentSearchResultClickEvent,
    segmentSearchResultImpressionEvent
} from '@zoopla/listing-search/segment';
import { useOptimizelyExperiment } from '@zoopla/optimizely';
import { Filters } from '@zoopla/redux-reducer-filters';
import Head from 'next/head';
import * as React from 'react';
import { FC, MouseEvent } from 'react';
import AdSlotDevicePair from './AdSlotDevicePair';
import { ErrorArea } from './ErrorArea';
import {
    agentContactTracking,
    agentLogoTracking,
    ecommerceClickTracking,
    getEcommerceDataForListing,
    imageGalleryControlTracking,
    imageTracking,
    listingContentTracking,
    phoneNumberRevealTracking
} from './Listings.tracking';
import { Listing as ListingType } from './Listings.types';
import {
    galleryLinkStyle,
    detailsPageLinkStyle,
    layoutContentOverlayStyle,
    layoutContentStyle
} from './index.styles.css';
import {
    listingRowStyle,
    listingsContainerStyle,
    mediaImageStyle
} from './Listings.styles.css';
import { bodyStyle, cardStyle, titleStyle } from './NoResultsBanner.css';
import { fontGroups } from '@zoopla-eng/zds/themes';
import { Gallery as ZDSGallery } from '@zoopla-eng/zds/gallery';
import { PlaceholderImage } from '@zoopla/assets';

const responsiveSizesPage =
    '(min-width: 1280px) 800px, (min-width: 960px) 66vw, 100vw';

type TrackingHandler = (props: {
    price?: string | null;
    listingId?: string | null;
}) => void;

type Props = {
    searchType: SearchContextType['searchType'];
    analyticsTaxonomyIdentifier: AnalyticsTaxonomy['searchIdentifier'];
    analyticsTaxonomy?: AnalyticsTaxonomy;
    searchCategory: SearchCategory;
    filters: Filters;
    currentSearchSource: SearchSource;
    error?: ApolloError;
    regularListings: ListingType[];
    extendedListings: ListingType[];
    onResetFilters: (evt: MouseEvent<HTMLButtonElement>) => void;
    favouritesListings: Favourites['listings'];
    authenticateSaveToggle: (
        listingId: string,
        onSuccess: ({ isSaved }: { isSaved: boolean }) => void
    ) => Promise<boolean>;

    authModal: React.ReactElement;
    analyticsEcommerce?: AnalyticsEcommerce;
    pageNumber: number;
    showHalo: boolean;
    route: string;
};

const onCardClickCommon = (
    listingId: string,
    searchCategory: SearchCategory,
    filters: Filters,
    currentSearchSource: SearchSource,
    price: string,
    pageNumber: number,
    analyticsEcommerce?: AnalyticsEcommerce,
    analyticsTaxonomy?: AnalyticsTaxonomy
) => {
    const ecommerceData =
        analyticsEcommerce &&
        getEcommerceDataForListing(analyticsEcommerce, listingId);

    if (ecommerceData) {
        ecommerceClickTracking(ecommerceData);
    }

    if (analyticsTaxonomy) {
        segmentSearchResultClickEvent(
            analyticsTaxonomy,
            currentSearchSource,
            searchCategory,
            filters,
            listingId,
            price,
            pageNumber
        );
    }
};

export const Listings: FC<React.PropsWithChildren<Props>> = ({
    searchType,
    analyticsTaxonomyIdentifier,
    analyticsTaxonomy,
    analyticsEcommerce,
    searchCategory,
    filters,
    currentSearchSource,
    error,
    regularListings,
    extendedListings,
    onResetFilters,
    favouritesListings,
    authenticateSaveToggle,
    authModal,
    pageNumber,
    showHalo,
    route
}) => {
    const [newHomesBoltOn] = useOptimizelyExperiment(
        'sr_1881_new_homes_bolt_on'
    );

    const isNewHomesBoltOnEnabled = newHomesBoltOn === 'variation_2';

    if (error && error?.message === 'AREA_NOT_FOUND') {
        return (
            <ErrorArea
                areaName={analyticsTaxonomy?.areaName}
                searchType={searchType}
            />
        );
    }

    if (error) {
        return (
            <div className={cardStyle}>
                <h2 className={`${titleStyle} ${fontGroups.mug}`}>
                    No results found
                </h2>
                <p className={`${bodyStyle} ${fontGroups.whisk}`}>
                    Please amend your search options or try searching again.
                </p>
            </div>
        );
    }

    const onContactClick: TrackingHandler = ({ price, listingId }) => {
        segmentSearchEvent({
            eventName: 'Click Email Agent',
            analyticsTaxonomy: analyticsTaxonomy as AnalyticsTaxonomy,
            currentSearchSource,
            searchCategory,
            filters,
            price,
            listingId,
            pageNumber
        });
        agentContactTracking();
    };

    const onPhoneClick: TrackingHandler = ({ price, listingId }) => {
        segmentSearchEvent({
            eventName: 'Click Call Agent',
            analyticsTaxonomy: analyticsTaxonomy as AnalyticsTaxonomy,
            currentSearchSource,
            searchCategory,
            filters,
            price,
            listingId,
            pageNumber
        });
    };

    const onSaveClick: TrackingHandler = ({ price, listingId }) => {
        if (!listingId) {
            console.error('Listing ID undefined: Unable to save listing');
            return;
        }

        const onSuccess = ({ isSaved }: { isSaved: boolean }): void => {
            if (isSaved) {
                segmentSearchEvent({
                    eventName: 'Click Save Listing',
                    analyticsTaxonomy: analyticsTaxonomy as AnalyticsTaxonomy,
                    currentSearchSource,
                    searchCategory,
                    filters,
                    price,
                    listingId,
                    pageNumber
                });

                segmentSearchEvent({
                    eventName: 'Save Listing',
                    analyticsTaxonomy: analyticsTaxonomy as AnalyticsTaxonomy,
                    currentSearchSource,
                    searchCategory,
                    filters,
                    price,
                    listingId,
                    pageNumber
                });
            } else if (!isSaved) {
                segmentSearchEvent({
                    eventName: 'Unsave Listing',
                    analyticsTaxonomy: analyticsTaxonomy as AnalyticsTaxonomy,
                    currentSearchSource,
                    searchCategory,
                    filters,
                    price,
                    listingId,
                    pageNumber
                });
            }
        };

        authenticateSaveToggle(listingId, onSuccess);
    };

    const onPhoneRevealClick: TrackingHandler = ({ price, listingId }) => {
        segmentSearchEvent({
            eventName: 'Phone Click To Reveal',
            analyticsTaxonomy: analyticsTaxonomy as AnalyticsTaxonomy,
            currentSearchSource,
            searchCategory,
            filters,
            price,
            listingId,
            pageNumber
        });
        phoneNumberRevealTracking();
    };

    const newHomesDevelopmentIds: { [key: string]: number } = {};

    return (
        <>
            {authModal}
            {Boolean(regularListings[0]?.image?.src) && (
                <Head>
                    <link
                        rel="preload"
                        as="image"
                        href={regularListings[0]?.image?.src ?? ''}
                        // imagesizes is not yet typed in our combo of typescript/react
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        imagesizes={responsiveSizesPage}
                    />

                    <link
                        rel="stylesheet"
                        href="https://cdn.prod.zoopla.co.uk/splide-core.min.css"
                    />
                </Head>
            )}
            {regularListings.length === 0 && extendedListings.length === 0 ? (
                <div className={cardStyle}>
                    <h2 className={`${titleStyle} ${fontGroups.mug}`}>
                        No results found
                    </h2>
                    <p className={`${bodyStyle} ${fontGroups.whisk}`}>
                        Please amend your search options or try searching again.
                    </p>
                    <ButtonLeft
                        category="primary"
                        Icon={RefreshMedium}
                        onClick={onResetFilters}
                    >
                        Reset filters
                    </ButtonLeft>
                </div>
            ) : (
                ''
            )}
            <div
                className={listingsContainerStyle}
                data-testid="regular-listings"
            >
                {regularListings.map((listing, index) => {
                    const branchId = listing.branch?.branchId;
                    const isNewHomesDevelopment = listing.isNewHomesDevelopment;

                    if (isNewHomesDevelopment && branchId) {
                        newHomesDevelopmentIds[branchId] =
                            newHomesDevelopmentIds[branchId] + 1 || 1;
                    }

                    const listingImages = getListingImages(listing);

                    return (
                        <div
                            key={`listing_${listing.listingId}`}
                            id={`listing_${listing.listingId}`}
                            className={listingRowStyle}
                        >
                            <ListingCard.InView
                                onInView={() => {
                                    if (!analyticsTaxonomy) return;

                                    segmentSearchResultImpressionEvent(
                                        analyticsTaxonomy,
                                        currentSearchSource,
                                        searchCategory,
                                        filters,
                                        listing.listingId,
                                        listing.price ?? '',
                                        pageNumber
                                    );
                                }}
                            >
                                <ListingCard.Layout
                                    type={getListingType(listing)}
                                >
                                    <ListingCard.LayoutMedia
                                        onClick={() => {
                                            onCardClickCommon(
                                                listing.listingId,
                                                searchCategory,
                                                filters,
                                                currentSearchSource,
                                                listing.price ?? '',
                                                pageNumber,
                                                analyticsEcommerce,
                                                analyticsTaxonomy
                                            );
                                            imageTracking();
                                        }}
                                    >
                                        <a
                                            className={galleryLinkStyle({
                                                isPremium:
                                                    getListingType(listing) ===
                                                    'premium'
                                            })}
                                            href={getDetailsLink(
                                                analyticsTaxonomyIdentifier,
                                                listing.listingUris
                                            )}
                                        >
                                            {listingImages.length ? (
                                                <ZDSGallery
                                                    images={listingImages}
                                                    type={getListingType(
                                                        listing
                                                    )}
                                                    objectFit="cover"
                                                    paginationAlignment="right"
                                                    numberOfFloorplans={
                                                        listing.numberOfFloorPlans ||
                                                        undefined
                                                    }
                                                    numberOfVideos={
                                                        listing.numberOfVideos ||
                                                        undefined
                                                    }
                                                    createCaption={(
                                                        index,
                                                        total
                                                    ) =>
                                                        `Property photo ${
                                                            index + 1
                                                        } of ${total}. ${
                                                            listingImages[index]
                                                                .caption
                                                        }`
                                                    }
                                                    onSlideNext={() =>
                                                        imageGalleryControlTracking(
                                                            listing.listingId
                                                        )
                                                    }
                                                    onSlidePrevious={() =>
                                                        imageGalleryControlTracking(
                                                            listing.listingId
                                                        )
                                                    }
                                                />
                                            ) : (
                                                <img
                                                    className={mediaImageStyle}
                                                    src={PlaceholderImage}
                                                    alt="Property images coming soon"
                                                    loading="lazy"
                                                />
                                            )}
                                        </a>
                                    </ListingCard.LayoutMedia>
                                    <ListingCard.LayoutContent
                                        onClick={() => {
                                            onCardClickCommon(
                                                listing.listingId,
                                                searchCategory,
                                                filters,
                                                currentSearchSource,
                                                listing.price ?? '',
                                                pageNumber,
                                                analyticsEcommerce,
                                                analyticsTaxonomy
                                            );
                                            listingContentTracking();
                                        }}
                                    >
                                        <div className={layoutContentStyle}>
                                            <div
                                                className={
                                                    layoutContentOverlayStyle
                                                }
                                            >
                                                <ListingCard.SaveListing
                                                    isSaved={favouritesListings.includes(
                                                        listing.listingId
                                                    )}
                                                    onSaveClick={() =>
                                                        onSaveClick({
                                                            price:
                                                                listing.price ??
                                                                '',
                                                            listingId:
                                                                listing.listingId
                                                        })
                                                    }
                                                />
                                            </div>
                                            <a
                                                className={detailsPageLinkStyle}
                                                href={getDetailsLink(
                                                    analyticsTaxonomyIdentifier,
                                                    listing.listingUris
                                                )}
                                            >
                                                <Box
                                                    pl="S400"
                                                    pr="S400"
                                                    height="100%"
                                                    pt="S400"
                                                >
                                                    <ListingCard.Status
                                                        value={getStatus(
                                                            listing
                                                        )}
                                                    />
                                                    <Box pb="S200">
                                                        <ListingCard.Price
                                                            reducedPriceDate={
                                                                listing
                                                                    .priceDrop
                                                                    ?.lastPriceChangeDate
                                                            }
                                                            reducedPricePercentage={
                                                                listing
                                                                    .priceDrop
                                                                    ?.percentageChangeLabel
                                                            }
                                                            priceTitle={
                                                                listing.priceTitle
                                                            }
                                                            price={
                                                                listing.price
                                                            }
                                                            priceAlternative={
                                                                listing.alternativeRentFrequencyLabel
                                                            }
                                                        />
                                                    </Box>
                                                    <Box pb="S400">
                                                        <ListingCard.Amenities
                                                            {...getAmenities(
                                                                listing
                                                            )}
                                                        />
                                                    </Box>
                                                    <ListingCard.Summary
                                                        description={
                                                            listing.title as string
                                                        }
                                                        address={
                                                            listing.address as string
                                                        }
                                                    />
                                                    <ListingCard.TransportLinks
                                                        transports={
                                                            listing.transports
                                                        }
                                                    />
                                                    <ListingCard.Badges
                                                        values={getBadgeValues(
                                                            listing
                                                        )}
                                                    />
                                                    <ListingCard.PremiumAttributes
                                                        attributes={getPremiumHighlights(
                                                            listing
                                                        )}
                                                        type={getListingType(
                                                            listing
                                                        )}
                                                    />
                                                    <ListingCard.Dates
                                                        availableFrom={
                                                            listing.availableFrom
                                                        }
                                                        availableFromLabel={
                                                            listing.availableFromLabel
                                                        }
                                                        publishedOn={
                                                            listing.publishedOn
                                                        }
                                                        publishedOnLabel={
                                                            listing.publishedOnLabel
                                                        }
                                                    />
                                                </Box>
                                            </a>
                                        </div>
                                    </ListingCard.LayoutContent>
                                    <ListingCard.LayoutFooterLeft>
                                        <ListingCard.AgentLogo
                                            href={
                                                listing.branch?.branchDetailsUri
                                            }
                                            src={listing.branch?.logoUrl}
                                            onClick={agentLogoTracking}
                                            title={listing.branch?.name}
                                        />
                                    </ListingCard.LayoutFooterLeft>
                                    <ListingCard.LayoutFooterRight>
                                        <ListingCard.ContactPhone
                                            branchId={listing.branch?.branchId}
                                            phoneNumber={listing.branch?.phone}
                                            listingId={listing.listingId}
                                            onPhoneNumberClick={() =>
                                                onPhoneClick({
                                                    price: listing.price,
                                                    listingId: listing.listingId
                                                })
                                            }
                                            onRevealPhoneNumberClick={() =>
                                                onPhoneRevealClick({
                                                    price: listing.price,
                                                    listingId: listing.listingId
                                                })
                                            }
                                        />
                                        <ListingCard.ContactEmail
                                            emailUrl={getBranchContactUrl(
                                                listing
                                            )}
                                            onClick={() =>
                                                onContactClick({
                                                    price: listing.price,
                                                    listingId: listing.listingId
                                                })
                                            }
                                        />
                                    </ListingCard.LayoutFooterRight>
                                    {isNewHomesBoltOnEnabled ? (
                                        isNewHomesDevelopment &&
                                        branchId &&
                                        newHomesDevelopmentIds[branchId] ===
                                            1 ? (
                                            <NewHomesListings
                                                parentListingId={
                                                    listing.listingId
                                                }
                                                branchId={branchId}
                                                searchIdentifier={
                                                    analyticsTaxonomyIdentifier
                                                }
                                                analyticsTaxonomy={
                                                    analyticsTaxonomy
                                                }
                                                searchSource={
                                                    currentSearchSource
                                                }
                                                searchCategory={searchCategory}
                                                filters={filters}
                                                pageNumber={pageNumber}
                                                developmentUrl={
                                                    listing.branch
                                                        ?.branchDetailsUri
                                                }
                                            />
                                        ) : null
                                    ) : null}
                                </ListingCard.Layout>
                            </ListingCard.InView>
                            <AdSlotDevicePair
                                route={route}
                                index={index}
                                uuid={'pn' + pageNumber + '_li' + index}
                            />
                        </div>
                    );
                })}

                {regularListings.length === 0 && extendedListings.length > 0 ? (
                    <div className={cardStyle}>
                        <h2 className={`${titleStyle} ${fontGroups.mug}`}>
                            No results found
                        </h2>
                        <p className={`${bodyStyle} ${fontGroups.whisk}`}>
                            However, we did find some properties nearby by
                            increasing your search radius.
                        </p>
                    </div>
                ) : (
                    ''
                )}

                {regularListings.length > 0 && extendedListings.length > 0 ? (
                    <div className={cardStyle}>
                        <h2 className={`${titleStyle} ${fontGroups.mug}`}>
                            No more exact results
                        </h2>
                        <p className={`${bodyStyle} ${fontGroups.whisk}`}>
                            However, we did find some properties nearby by
                            increasing your search radius.
                        </p>
                    </div>
                ) : (
                    ''
                )}

                {extendedListings.map((listing, index) => {
                    const listingImages = getListingImages(listing);
                    return (
                        <div
                            key={`listing_${listing.listingId}`}
                            id={`listing_${listing.listingId}`}
                            className={listingRowStyle}
                        >
                            <ListingCard.InView
                                onInView={() => {
                                    if (!analyticsTaxonomy) return;

                                    segmentSearchResultImpressionEvent(
                                        analyticsTaxonomy,
                                        currentSearchSource,
                                        searchCategory,
                                        filters,
                                        listing.listingId,
                                        listing.price ?? '',
                                        pageNumber
                                    );
                                }}
                            >
                                <ListingCard.Layout
                                    type={getListingType(listing)}
                                >
                                    <ListingCard.LayoutMedia
                                        onClick={() => {
                                            onCardClickCommon(
                                                listing.listingId,
                                                searchCategory,
                                                filters,
                                                currentSearchSource,
                                                listing.price ?? '',
                                                pageNumber,
                                                analyticsEcommerce,
                                                analyticsTaxonomy
                                            );
                                            imageTracking();
                                        }}
                                    >
                                        <a
                                            className={galleryLinkStyle({
                                                isPremium:
                                                    getListingType(listing) ===
                                                    'premium'
                                            })}
                                            href={getDetailsLink(
                                                analyticsTaxonomyIdentifier,
                                                listing.listingUris
                                            )}
                                        >
                                            {listingImages.length ? (
                                                <ZDSGallery
                                                    images={listingImages}
                                                    objectFit="cover"
                                                    type={getListingType(
                                                        listing
                                                    )}
                                                    paginationAlignment="right"
                                                    numberOfFloorplans={
                                                        listing.numberOfFloorPlans ||
                                                        undefined
                                                    }
                                                    numberOfVideos={
                                                        listing.numberOfVideos ||
                                                        undefined
                                                    }
                                                    createCaption={(
                                                        index,
                                                        total
                                                    ) =>
                                                        `Property photo ${
                                                            index + 1
                                                        } of ${total}. ${
                                                            listingImages[index]
                                                                .caption
                                                        }`
                                                    }
                                                    onSlideNext={() =>
                                                        imageGalleryControlTracking(
                                                            listing.listingId
                                                        )
                                                    }
                                                    onSlidePrevious={() =>
                                                        imageGalleryControlTracking(
                                                            listing.listingId
                                                        )
                                                    }
                                                />
                                            ) : (
                                                <img
                                                    className={mediaImageStyle}
                                                    src={PlaceholderImage}
                                                    alt="Property images coming soon"
                                                    loading="lazy"
                                                />
                                            )}
                                        </a>
                                    </ListingCard.LayoutMedia>
                                    <ListingCard.LayoutContent
                                        onClick={() => {
                                            onCardClickCommon(
                                                listing.listingId,
                                                searchCategory,
                                                filters,
                                                currentSearchSource,
                                                listing.price ?? '',
                                                pageNumber,
                                                analyticsEcommerce,
                                                analyticsTaxonomy
                                            );
                                            listingContentTracking();
                                        }}
                                    >
                                        <div className={layoutContentStyle}>
                                            <div
                                                className={
                                                    layoutContentOverlayStyle
                                                }
                                            >
                                                <ListingCard.SaveListing
                                                    isSaved={favouritesListings.includes(
                                                        listing.listingId
                                                    )}
                                                    onSaveClick={() =>
                                                        onSaveClick({
                                                            price:
                                                                listing.price ??
                                                                '',
                                                            listingId:
                                                                listing.listingId
                                                        })
                                                    }
                                                />
                                            </div>
                                            <a
                                                className={detailsPageLinkStyle}
                                                href={getDetailsLink(
                                                    analyticsTaxonomyIdentifier,
                                                    listing.listingUris
                                                )}
                                            >
                                                <Box p="S400" height="100%">
                                                    <ListingCard.Status
                                                        value={getStatus(
                                                            listing
                                                        )}
                                                    />
                                                    <ListingCard.Price
                                                        reducedPriceDate={
                                                            listing.priceDrop
                                                                ?.lastPriceChangeDate
                                                        }
                                                        reducedPricePercentage={
                                                            listing.priceDrop
                                                                ?.percentageChangeLabel
                                                        }
                                                        priceTitle={
                                                            listing.priceTitle
                                                        }
                                                        price={listing.price}
                                                        priceAlternative={
                                                            listing.alternativeRentFrequencyLabel
                                                        }
                                                    />
                                                    <ListingCard.Amenities
                                                        {...getAmenities(
                                                            listing
                                                        )}
                                                    />
                                                    <ListingCard.Summary
                                                        description={
                                                            listing.title as string
                                                        }
                                                        address={
                                                            listing.address as string
                                                        }
                                                    />
                                                    <ListingCard.TransportLinks
                                                        transports={
                                                            listing.transports
                                                        }
                                                    />
                                                    <ListingCard.Badges
                                                        values={getBadgeValues(
                                                            listing
                                                        )}
                                                    />
                                                    <ListingCard.PremiumAttributes
                                                        attributes={getPremiumHighlights(
                                                            listing
                                                        )}
                                                        type={getListingType(
                                                            listing
                                                        )}
                                                    />
                                                    <ListingCard.Dates
                                                        availableFrom={
                                                            listing.availableFrom
                                                        }
                                                        availableFromLabel={
                                                            listing.availableFromLabel
                                                        }
                                                        publishedOn={
                                                            listing.publishedOn
                                                        }
                                                        publishedOnLabel={
                                                            listing.publishedOnLabel
                                                        }
                                                    />
                                                </Box>
                                            </a>
                                        </div>
                                    </ListingCard.LayoutContent>
                                    <ListingCard.LayoutFooterLeft>
                                        <ListingCard.AgentLogo
                                            href={
                                                listing.branch?.branchDetailsUri
                                            }
                                            src={listing.branch?.logoUrl}
                                            onClick={agentLogoTracking}
                                            title={listing.branch?.name}
                                        />
                                    </ListingCard.LayoutFooterLeft>
                                    <ListingCard.LayoutFooterRight>
                                        <ListingCard.ContactPhone
                                            branchId={listing.branch?.branchId}
                                            phoneNumber={listing.branch?.phone}
                                            listingId={listing.listingId}
                                            onPhoneNumberClick={() =>
                                                onPhoneClick({
                                                    price: listing.price,
                                                    listingId: listing.listingId
                                                })
                                            }
                                            onRevealPhoneNumberClick={() =>
                                                onPhoneRevealClick({
                                                    price: listing.price,
                                                    listingId: listing.listingId
                                                })
                                            }
                                        />
                                        <ListingCard.ContactEmail
                                            emailUrl={getBranchContactUrl(
                                                listing
                                            )}
                                            onClick={() =>
                                                onContactClick({
                                                    price: listing.price,
                                                    listingId: listing.listingId
                                                })
                                            }
                                        />
                                    </ListingCard.LayoutFooterRight>
                                </ListingCard.Layout>
                            </ListingCard.InView>
                            <AdSlotDevicePair
                                route={route}
                                index={index}
                                // Note: To avoid potential ID clashes with regular interstitial ads,
                                //       introduce a constant uuid value.
                                uuid={'extended_pn' + pageNumber + '_' + index}
                            />
                        </div>
                    );
                })}
            </div>
        </>
    );
};
